// Submissions table columns — the serial number (`sr`, stamped by the endpoint
// transform, not derived from the row's position), the source record's identity, the
// submitter, the structure node it was filed under, the absolute submitted
// datetime, the flow-state chip, and the three trailing icon actions — REPORT
// opens the submission's rendered report, COMMENTS and LOGS are still
// coming-soon. There is no kebab: the design's row controls are those three
// icons.
//
// Only ID & REF NO., DATE SUBMITTED and STATUS carry `sortable` — those are the
// three keys the endpoint's `orderBy` accepts, and their column keys ARE the
// SubmissionSortKey members rather than UI-local names, so the header the user
// clicks and the value the body carries cannot drift apart. The mockup also draws
// chevrons on NAME and SUBMITTED BY; they are omitted rather than rendered inert,
// since DataTable draws a glyph for any sortable column and a header that cannot
// sort is a dead affordance.

import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Chip,
  Icon,
  IconButton,
  type ChipVariant,
  type DataTableColumn,
} from '@/components/common';
import {
  SubmissionFlowState,
  SubmissionSortKey,
  SubmissionSourceFilter,
  type Submission,
} from '@/api/endpoints/submissionsApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import i18n from '@/i18n/config';

/** Flow state → chip tone + glyph + label, per the design's three chips. The
 *  wire enum is confirmed, so this is exhaustive — no fallback branch. */
export const FLOW_STATE_META: Record<
  SubmissionFlowState,
  { chip: ChipVariant; icon: string; labelKey: string }
> = {
  [SubmissionFlowState.End]: {
    chip: 'success',
    icon: 'circle-check',
    labelKey: 'submissions_state_end',
  },
  [SubmissionFlowState.User]: {
    chip: 'warning',
    icon: 'reply',
    labelKey: 'submissions_state_user',
  },
  [SubmissionFlowState.Step]: {
    chip: 'info',
    icon: 'clock',
    labelKey: 'submissions_state_step',
  },
};

const dash = <span className='submissions-table__dash'>—</span>;

/** The source record's display code, shared with the report view's header so the
 *  two never drift. The table and report call sites still default to `INSP-`:
 *  neither of their payloads carries a source type yet (the pdf-history
 *  endpoint's `linkedType` proves the backend CAN ship one), so only the PDF
 *  versions modal passes `type` and renders `TK-` correctly. */
const SOURCE_PREFIX: Record<SubmissionSourceFilter, string> = {
  [SubmissionSourceFilter.Inspection]: 'INSP',
  [SubmissionSourceFilter.Task]: 'TK',
};
export const fmtSourceCode = (
  referenceNumber: number,
  type: SubmissionSourceFilter = SubmissionSourceFilter.Inspection,
): string => `${SOURCE_PREFIX[type]}-${String(referenceNumber).padStart(2, '0')}`;

// Absolute date + time. Intl emits a comma between the two, never the design's
// "·", so the parts are formatted separately and joined through an i18n key —
// the same idiom as reportColumns' fmtDateTime.
export const fmtDateTime = (
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return t('submissions_datetime', { date, time });
};

// The three trailing icon columns. A surface where all three would be inert
// passes these as SubmissionsList's `hiddenColumns` so none of them renders.
export const SUBMISSION_ICON_COLUMN_KEYS = ['report', 'comments', 'logs'];

interface UseSubmissionColumnsArgs {
  /** COMMENTS and LOGS are still coming-soon. */
  onSoon: () => void;
  /** Open the row's rendered report. */
  onOpenReport: (submission: Submission) => void;
}

export const useSubmissionColumns = ({
  onSoon,
  onOpenReport,
}: UseSubmissionColumnsArgs): DataTableColumn<Submission>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'ordinal',
      label: '#',
      width: '3.5rem',
      // `sr` already counts across pages — stamped in the endpoint's transform.
      render: (s) => <span className='submissions-table__ordinal'>{s.sr}</span>,
    },
    {
      key: SubmissionSortKey.ReferenceNumber,
      label: t('submissions_col_ref'),
      width: '12rem',
      sortable: true,
      // One line: a glyph tile, the source record's code, a dot, then the
      // submission's own reference in bold. Both halves dash together when the
      // submission carries no source record.
      render: (s) =>
        s.source ? (
          <span className='submissions-table__ref'>
            <span className='submissions-table__ref-tile' aria-hidden='true'>
              <Icon name='clipboard-check' size={16} />
            </span>
            <span className='submissions-table__ref-code'>
              {fmtSourceCode(s.source.referenceNumber)}
            </span>
            <span className='submissions-table__ref-sep' aria-hidden='true'>
              ·
            </span>
            <span className='submissions-table__ref-no'>{s.referenceNumber}</span>
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'name',
      label: t('submissions_col_name'),
      width: 'minmax(13rem, 1.4fr)',
      render: (s) =>
        s.source ? <span className='submissions-table__name'>{s.source.title}</span> : dash,
    },
    {
      key: 'submittedBy',
      label: t('submissions_col_by'),
      width: 'minmax(11rem, 1fr)',
      render: (s) =>
        s.submittedBy ? (
          <span className='submissions-table__person'>
            <Avatar
              initials={initialsOf(s.submittedBy.fullName)}
              size={36}
              tone={toneOf(s.submittedBy.fullName)}
            />
            <span className='submissions-table__person-name'>{s.submittedBy.fullName}</span>
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'node',
      label: t('submissions_col_node'),
      width: 'minmax(11rem, 1fr)',
      // Three stacked lines beside the level's own glyph: the node, its level,
      // then its parent — the parent line is dropped for a root node.
      render: (s) =>
        s.node ? (
          <span className='submissions-table__node'>
            <span className='submissions-table__node-icon' aria-hidden='true'>
              <Icon name={s.node.levelIcon} size={17} />
            </span>
            <span className='submissions-table__node-lines'>
              <span className='submissions-table__node-name'>{s.node.name}</span>
              {s.node.levelName && (
                <span className='submissions-table__node-level'>{s.node.levelName}</span>
              )}
              {s.node.parentName && (
                <span className='submissions-table__node-parent'>{s.node.parentName}</span>
              )}
            </span>
          </span>
        ) : (
          dash
        ),
    },
    {
      key: SubmissionSortKey.CompleteDate,
      label: t('submissions_col_date'),
      width: '11rem',
      sortable: true,
      render: (s) => (
        <span className='submissions-table__muted'>{fmtDateTime(s.completeDate, t)}</span>
      ),
    },
    {
      key: SubmissionSortKey.FlowState,
      label: t('submissions_col_status'),
      width: '9rem',
      sortable: true,
      render: (s) => {
        const meta = FLOW_STATE_META[s.submissionFlowState];
        return (
          <Chip variant={meta.chip} iconStart={meta.icon}>
            {t(meta.labelKey)}
          </Chip>
        );
      },
    },
    {
      key: 'report',
      label: t('submissions_col_report'),
      align: 'end',
      width: '5rem',
      render: (s) => (
        <IconButton
          icon='external-link'
          variant='ghost'
          aria-label={t('submissions_col_report')}
          onClick={() => onOpenReport(s)}
        />
      ),
    },
    {
      key: 'comments',
      label: t('submissions_col_comments'),
      align: 'end',
      width: '6rem',
      // IconButton takes no children and nothing in the app overlays a count on
      // one, so the badge is a Chip composed over it (convention: badges = Chip)
      // and positioned with a logical inset so it mirrors under RTL.
      render: (s) => (
        <span className='submissions-table__comments'>
          <IconButton
            icon='message-square'
            variant='ghost'
            aria-label={t('submissions_col_comments')}
            onClick={onSoon}
          />
          {s.comments > 0 && (
            <Chip variant='brand-solid' className='submissions-table__comments-count'>
              {s.comments}
            </Chip>
          )}
        </span>
      ),
    },
    {
      key: 'logs',
      label: t('submissions_col_logs'),
      align: 'end',
      width: '5rem',
      render: () => (
        <IconButton
          icon='scroll-text'
          variant='ghost'
          aria-label={t('submissions_col_logs')}
          onClick={onSoon}
        />
      ),
    },
  ];
};
